import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../services/apiClient';
import { extractActivationKeys } from '../utils/apiHelpers';

const MyLicensesPage = () => {
  const [activationKeys, setActivationKeys] = useState([]);
  const [clusterLicenses, setClusterLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'activation', 'cluster'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Set page title
  useEffect(() => {
    document.title = 'My Licenses | Customer Portal';
  }, []);

  useEffect(() => {
    loadAllLicenses();
  }, []);

  const loadAllLicenses = async () => {
    try {
      // Load approved activation keys using apiClient
      const activationData = await apiClient.get('/activation-keys/my-keys');
      const activationKeys = extractActivationKeys(activationData);
      // Filter only APPROVED activation keys
      setActivationKeys(activationKeys.filter(k => k.status === 'APPROVED' && k.signedFilePath));
      
      // Load cluster licenses - apiClient already extracts response.data.data
      const clusterLicenses = await apiClient.get('/cluster-licenses/my-licenses');
      // Filter only APPROVED cluster licenses (status is APPROVED, not SIGNED)
      const approvedClusterLicenses = Array.isArray(clusterLicenses) 
        ? clusterLicenses.filter(c => c.status === 'APPROVED' && c.signedFilePath)
        : [];
      setClusterLicenses(approvedClusterLicenses);
    } catch (error) {
      console.error('Error loading licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadActivationKey = async (id, hostname) => {
    try {
      const blob = await apiClient.get(`/activation-keys/download/${id}`, {
        responseType: 'blob'
      });

      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activation-${hostname}-signed.xlic`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Downloaded signed activation key');
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const downloadClusterLicense = async (id, hostname) => {
    try {
      const blob = await apiClient.get(`/cluster-licenses/download/${id}`, {
        responseType: 'blob'
      });

      if (blob && blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cluster-license-${hostname}-signed.xlic`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Downloaded cluster license');
      } else {
        toast.error('Failed to download cluster license');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download cluster license');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFilteredLicenses = () => {
    let combined = [];
    
    if (filter === 'all' || filter === 'activation') {
      combined = [...combined, ...activationKeys.map(k => ({...k, type: 'activation'}))];
    }
    
    if (filter === 'all' || filter === 'cluster') {
      combined = [...combined, ...clusterLicenses.map(c => ({...c, type: 'cluster'}))];
    }
    
    // Sort by date
    combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return combined;
  };

  const filteredLicenses = getFilteredLicenses();
  const totalCount = activationKeys.length + clusterLicenses.length;
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredLicenses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLicenses = filteredLicenses.slice(startIndex, endIndex);
  
  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading your licenses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">My Licenses</h2>
            <span className="text-sm font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-lg">
              {totalCount} license{totalCount !== 1 ? 's' : ''} total
            </span>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === 'all' ? { backgroundColor: '#197560' } : {}}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setFilter('activation')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'activation'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === 'activation' ? { backgroundColor: '#197560' } : {}}
            >
              Activation Keys - XDeploy ({activationKeys.length})
            </button>
            <button
              onClick={() => setFilter('cluster')}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'cluster'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === 'cluster' ? { backgroundColor: '#197560' } : {}}
            >
              Cluster Licenses ({clusterLicenses.length})
            </button>
          </div>
        </div>

        {filteredLicenses.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-16 h-16 text-gray-400 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {filter === 'all' && 'No Licenses Yet'}
              {filter === 'activation' && 'No Activation Keys Yet'}
              {filter === 'cluster' && 'No Cluster Licenses Yet'}
            </h2>
            <p className="text-gray-600 mb-6">
              {filter === 'all' && 'Once your requests are approved, licenses will appear here.'}
              {filter === 'activation' && 'Approved activation keys for XDeploy access will be available here.'}
              {filter === 'cluster' && 'Signed cluster licenses for multi-node deployments will be available here.'}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Getting Started</p>
                  <p>Submit license requests or upload activation keys. Approved items will be downloadable from here.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      License Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      System Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Issued Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedLicenses.map((item) => (
                    <tr key={`${item.type}-${item.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          item.type === 'activation'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {item.type === 'activation' ? 'XDeploy' : 'Cluster License'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {item.hostname || 'Unknown Host'}
                        </div>
                        {item.type === 'activation' && item.originalFilename && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {item.originalFilename}
                          </div>
                        )}
                        {item.type === 'cluster' && item.originalFilename && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {item.originalFilename}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {item.type === 'activation' && item.cpuSockets && item.cpuCores ? (
                          <div>{item.cpuSockets} sockets, {item.cpuCores} cores</div>
                        ) : item.type === 'cluster' && item.totalHosts ? (
                          <div>{item.totalHosts} hosts, {item.totalSockets} sockets</div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {item.type === 'activation' && (
                          <button
                            onClick={() => downloadActivationKey(item.id, item.hostname)}
                            className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </button>
                        )}
                        {item.type === 'cluster' && (
                          <button
                            onClick={() => downloadClusterLicense(item.id, item.hostname)}
                            className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {paginatedLicenses.map((item) => (
                <div key={`${item.type}-${item.id}`} className="bg-white rounded-xl border border-gray-100 p-4 shadow-md hover:shadow-lg transition-all duration-300 hover:border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full shadow-sm ${
                      item.type === 'activation'
                        ? 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-800 border border-purple-200'
                        : 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200'
                    }`}>
                      {item.type === 'activation' ? 'XDeploy' : 'Cluster License'}
                    </span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded">{formatDate(item.createdAt)}</span>
                  </div>
                  
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">License Details</p>
                      <p className="text-sm font-medium text-gray-900">
                        {item.hostname || 'Unknown Host'}
                      </p>
                      {item.type === 'activation' && item.originalFilename && (
                        <p className="text-xs text-gray-500 truncate">
                          {item.originalFilename}
                        </p>
                      )}
                      {item.type === 'cluster' && item.originalFilename && (
                        <p className="text-xs text-gray-500 truncate">
                          {item.originalFilename}
                        </p>
                      )}
                    </div>
                    
                    {item.type === 'activation' && item.cpuSockets && item.cpuCores && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">System Info</p>
                        <p className="text-sm text-gray-700">{item.cpuSockets} sockets, {item.cpuCores} cores</p>
                      </div>
                    )}
                    
                    {item.type === 'cluster' && item.totalHosts && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Cluster Info</p>
                        <p className="text-sm text-gray-700">{item.totalHosts} hosts, {item.totalSockets} sockets</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    {item.type === 'activation' && (
                      <button
                        onClick={() => downloadActivationKey(item.id, item.hostname)}
                        className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                        style={{ backgroundColor: '#197560' }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download XDeploy License
                      </button>
                    )}
                    {item.type === 'cluster' && (
                      <button
                        onClick={() => downloadClusterLicense(item.id, item.hostname)}
                        className="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white rounded-xl hover:opacity-90 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                        style={{ backgroundColor: '#197560' }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Cluster License
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Pagination Controls */}
            {filteredLicenses.length > itemsPerPage && (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing {Math.min(startIndex + 1, filteredLicenses.length)} to {Math.min(endIndex, filteredLicenses.length)} of {filteredLicenses.length} licenses
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  {(() => {
                    const pages = [];
                    for (let i = 1; i <= totalPages; i++) {
                      if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-1 border rounded text-sm transition-colors ${
                              currentPage === i
                                ? 'bg-green-600 text-white border-green-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      } else if (i === currentPage - 2 || i === currentPage + 2) {
                        pages.push(<span key={i} className="px-2 text-gray-500">...</span>);
                      }
                    }
                    return pages;
                  })()}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyLicensesPage;